import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './FloatButton.module.css';

const FloatingButton = () => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [isStatic, setIsStatic] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    city: '',
    experience: '',
    goal: '',
    timeCommitment: '',
    disciplineAware: '',
    openQuestion: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [agreed, setAgreed] = useState(false);
  
  const buttonRef = useRef(null);

  const serverUrl = "https://dynamic-server-dfc88e1f1c54.herokuapp.com/leads/newLead";
  const reciver = "riseupnon@gmail.com";

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // הצג את הכפתור רק אחרי 200px גלילה
      if (scrollY > 200) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
      
      // בדוק אם הגענו לתחתית הדף (עם מרווח של 100px)
      if (scrollY + windowHeight >= documentHeight - 100) {
        setIsStatic(true);
      } else {
        setIsStatic(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // בדיקה ראשונית

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleClick = () => {
    setShowContactForm(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'נא להזין שם מלא';
    }

    const phoneRegex = /^0(5\d|[23489])\d{7}$/;
    if (!formData.phone.trim()) {
      newErrors.phone = 'נא להזין מספר טלפון';
    } else if (!phoneRegex.test(formData.phone.replace(/[-\s]/g, ''))) {
      newErrors.phone = 'נא להזין מספר טלפון תקין';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'נא להזין אימייל';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'נא להזין אימייל תקין';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'נא להזין עיר';
    }

    if (!formData.experience) {
      newErrors.experience = 'נא לבחור תשובה';
    }

    if (!formData.goal) {
      newErrors.goal = 'נא לבחור תשובה';
    }

    if (!formData.timeCommitment) {
      newErrors.timeCommitment = 'נא לבחור תשובה';
    }

    if (!formData.disciplineAware) {
      newErrors.disciplineAware = 'נא לבחור תשובה';
    }

    if (!agreed) {
      newErrors.agreed = 'יש לאשר את תנאי השימוש';
    }

    setErrors(newErrors);
    
    if (Object.keys(newErrors).length > 0) {
      const firstErrorField = Object.keys(newErrors)[0];
      setTimeout(() => {
        const errorElement = document.querySelector(`[name="${firstErrorField}"]`) || 
                            document.querySelector(`.${styles.errorText}`);
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (errorElement.focus) {
            errorElement.focus();
          }
        }
      }, 100);
    }
    
    return Object.keys(newErrors).length === 0;
  };

  const handleContactSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    const detailedInfo = `
עיר: ${formData.city}
---
1. ניסיון במסחר: ${formData.experience}
2. מטרה עיקרית: ${formData.goal}
3. זמן שבועי ללמידה: ${formData.timeCommitment}
4. מודעות למשמעת נדרשת: ${formData.disciplineAware}
5. שאלה פתוחה: ${formData.openQuestion || 'לא צוין'}
    `.trim();

    const serverData = {
      name: formData.fullName,
      phone: formData.phone.replace(/[-\s]/g, ''),
      email: formData.email,
      reason: detailedInfo,
      reciver: reciver
    };

    try {
      const response = await fetch(serverUrl, {
        method: "POST",
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(serverData)
      });

      if (response.ok) {
        setSubmitted(true);
        
        // מעבר לדף תודה אחרי 1.5 שניות
        setTimeout(() => {
          navigate('/תודה');
        }, 1500);
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      alert("התרחשה שגיאה, אנא נסו שוב מאוחר יותר");
      console.error('Error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div
        ref={buttonRef}
        className={`${styles.floatingButton} ${isVisible ? styles.visible : ''} ${isStatic ? styles.static : ''}`}
        onClick={handleClick}
      >
        <div className={styles.shine}></div>
        <span className={styles.buttonText}>
          לחץ כאן כדי לשמוע פרטים
        </span>
      </div>

      {/* Contact Form Overlay */}
      <div className={`${styles.contactOverlay} ${showContactForm ? styles.active : ''}`}>
        <div className={styles.contactForm}>
          <button className={styles.closeButton} onClick={() => setShowContactForm(false)}>
            ×
          </button>

          <div className={styles.formHeader}>
            <h2 className={styles.formTitle}>מלא את הטופס וקבל התאמה אישית</h2>
            <p className={styles.formSubtitle}>למסלול מסחר מקצועי עם חשבון ממומן</p>
          </div>

          <div className={styles.formScrollContainer}>
            {/* Personal Details Section */}
            <div className={styles.formSection}>
              <h3 className={styles.sectionTitle}>
                <span className={styles.sectionIcon}>
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                  </svg>
                </span>
                פרטים אישיים
              </h3>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>שם מלא</label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className={`${styles.formInput} ${errors.fullName ? styles.inputError : ''}`}
                    placeholder="השם המלא שלך"
                    disabled={isSubmitting || submitted}
                  />
                  {errors.fullName && <p className={styles.errorText}>{errors.fullName}</p>}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>טלפון</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className={`${styles.formInput} ${errors.phone ? styles.inputError : ''}`}
                    placeholder="050-1234567"
                    disabled={isSubmitting || submitted}
                  />
                  {errors.phone && <p className={styles.errorText}>{errors.phone}</p>}
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>אימייל</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`${styles.formInput} ${errors.email ? styles.inputError : ''}`}
                    placeholder="your@email.com"
                    disabled={isSubmitting || submitted}
                  />
                  {errors.email && <p className={styles.errorText}>{errors.email}</p>}
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>עיר</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className={`${styles.formInput} ${errors.city ? styles.inputError : ''}`}
                    placeholder="עיר מגורים"
                    disabled={isSubmitting || submitted}
                  />
                  {errors.city && <p className={styles.errorText}>{errors.city}</p>}
                </div>
              </div>
            </div>

            {/* Question 1 */}
            <div className={styles.formSection}>
              <h3 className={styles.questionTitle}>
                <span className={styles.questionNumber}>1</span>
                איזה ניסיון יש לך במסחר בשוק ההון?
              </h3>
              <div className={styles.radioGroup}>
                <label className={`${styles.radioOption} ${formData.experience === 'אין לי ניסיון בכלל' ? styles.radioSelected : ''}`}>
                  <input
                    type="radio"
                    name="experience"
                    value="אין לי ניסיון בכלל"
                    checked={formData.experience === 'אין לי ניסיון בכלל'}
                    onChange={handleInputChange}
                    disabled={isSubmitting || submitted}
                  />
                  <span className={styles.radioCircle}></span>
                  <span className={styles.radioText}>אין לי ניסיון בכלל</span>
                </label>
                <label className={`${styles.radioOption} ${formData.experience === 'סוחר מעט, אך עדיין לא עקבי' ? styles.radioSelected : ''}`}>
                  <input
                    type="radio"
                    name="experience"
                    value="סוחר מעט, אך עדיין לא עקבי"
                    checked={formData.experience === 'סוחר מעט, אך עדיין לא עקבי'}
                    onChange={handleInputChange}
                    disabled={isSubmitting || submitted}
                  />
                  <span className={styles.radioCircle}></span>
                  <span className={styles.radioText}>סוחר מעט, אך עדיין לא עקבי</span>
                </label>
                <label className={`${styles.radioOption} ${formData.experience === 'יש לי ניסיון ורוצה להתקדם למסחר מקצועי עם חשבונות ממומנים' ? styles.radioSelected : ''}`}>
                  <input
                    type="radio"
                    name="experience"
                    value="יש לי ניסיון ורוצה להתקדם למסחר מקצועי עם חשבונות ממומנים"
                    checked={formData.experience === 'יש לי ניסיון ורוצה להתקדם למסחר מקצועי עם חשבונות ממומנים'}
                    onChange={handleInputChange}
                    disabled={isSubmitting || submitted}
                  />
                  <span className={styles.radioCircle}></span>
                  <span className={styles.radioText}>יש לי ניסיון ורוצה להתקדם למסחר מקצועי עם חשבונות ממומנים</span>
                </label>
              </div>
              {errors.experience && <p className={styles.errorText}>{errors.experience}</p>}
            </div>

            {/* Question 2 */}
            <div className={styles.formSection}>
              <h3 className={styles.questionTitle}>
                <span className={styles.questionNumber}>2</span>
                מה המטרה העיקרית שלך בלימודי מסחר?
              </h3>
              <div className={styles.radioGroup}>
                <label className={`${styles.radioOption} ${formData.goal === 'יצירת הכנסה נוספת מהצד' ? styles.radioSelected : ''}`}>
                  <input
                    type="radio"
                    name="goal"
                    value="יצירת הכנסה נוספת מהצד"
                    checked={formData.goal === 'יצירת הכנסה נוספת מהצד'}
                    onChange={handleInputChange}
                    disabled={isSubmitting || submitted}
                  />
                  <span className={styles.radioCircle}></span>
                  <span className={styles.radioText}>יצירת הכנסה נוספת מהצד</span>
                </label>
                <label className={`${styles.radioOption} ${formData.goal === 'הפיכת המסחר לקריירה עיקרית' ? styles.radioSelected : ''}`}>
                  <input
                    type="radio"
                    name="goal"
                    value="הפיכת המסחר לקריירה עיקרית"
                    checked={formData.goal === 'הפיכת המסחר לקריירה עיקרית'}
                    onChange={handleInputChange}
                    disabled={isSubmitting || submitted}
                  />
                  <span className={styles.radioCircle}></span>
                  <span className={styles.radioText}>הפיכת המסחר לקריירה עיקרית</span>
                </label>
                <label className={`${styles.radioOption} ${formData.goal === 'עמידה בתנאי חשבון ממומן וקבלת הקצאת הון' ? styles.radioSelected : ''}`}>
                  <input
                    type="radio"
                    name="goal"
                    value="עמידה בתנאי חשבון ממומן וקבלת הקצאת הון"
                    checked={formData.goal === 'עמידה בתנאי חשבון ממומן וקבלת הקצאת הון'}
                    onChange={handleInputChange}
                    disabled={isSubmitting || submitted}
                  />
                  <span className={styles.radioCircle}></span>
                  <span className={styles.radioText}>עמידה בתנאי חשבון ממומן וקבלת הקצאת הון</span>
                </label>
              </div>
              {errors.goal && <p className={styles.errorText}>{errors.goal}</p>}
            </div>

            {/* Question 3 */}
            <div className={styles.formSection}>
              <h3 className={styles.questionTitle}>
                <span className={styles.questionNumber}>3</span>
                כמה זמן ריאלי אתה מוכן להקדיש ללמידה ולתרגול בשבוע?
              </h3>
              <div className={styles.radioGroup}>
                <label className={`${styles.radioOption} ${formData.timeCommitment === 'עד שעתיים' ? styles.radioSelected : ''}`}>
                  <input
                    type="radio"
                    name="timeCommitment"
                    value="עד שעתיים"
                    checked={formData.timeCommitment === 'עד שעתיים'}
                    onChange={handleInputChange}
                    disabled={isSubmitting || submitted}
                  />
                  <span className={styles.radioCircle}></span>
                  <span className={styles.radioText}>עד שעתיים</span>
                </label>
                <label className={`${styles.radioOption} ${formData.timeCommitment === '3-5 שעות' ? styles.radioSelected : ''}`}>
                  <input
                    type="radio"
                    name="timeCommitment"
                    value="3-5 שעות"
                    checked={formData.timeCommitment === '3-5 שעות'}
                    onChange={handleInputChange}
                    disabled={isSubmitting || submitted}
                  />
                  <span className={styles.radioCircle}></span>
                  <span className={styles.radioText}>3-5 שעות</span>
                </label>
                <label className={`${styles.radioOption} ${formData.timeCommitment === 'יותר מ-5 שעות' ? styles.radioSelected : ''}`}>
                  <input
                    type="radio"
                    name="timeCommitment"
                    value="יותר מ-5 שעות"
                    checked={formData.timeCommitment === 'יותר מ-5 שעות'}
                    onChange={handleInputChange}
                    disabled={isSubmitting || submitted}
                  />
                  <span className={styles.radioCircle}></span>
                  <span className={styles.radioText}>יותר מ-5 שעות</span>
                </label>
              </div>
              {errors.timeCommitment && <p className={styles.errorText}>{errors.timeCommitment}</p>}
            </div>

            {/* Question 4 */}
            <div className={styles.formSection}>
              <h3 className={styles.questionTitle}>
                <span className={styles.questionNumber}>4</span>
                האם ברור לך שמדובר בתהליך לימודי ומעשי הדורש משמעת, תרגול והתמדה לאורך זמן?
              </h3>
              <div className={styles.radioGroup}>
                <label className={`${styles.radioOption} ${formData.disciplineAware === 'כן' ? styles.radioSelected : ''}`}>
                  <input
                    type="radio"
                    name="disciplineAware"
                    value="כן"
                    checked={formData.disciplineAware === 'כן'}
                    onChange={handleInputChange}
                    disabled={isSubmitting || submitted}
                  />
                  <span className={styles.radioCircle}></span>
                  <span className={styles.radioText}>כן</span>
                </label>
                <label className={`${styles.radioOption} ${formData.disciplineAware === 'לא בטוח' ? styles.radioSelected : ''}`}>
                  <input
                    type="radio"
                    name="disciplineAware"
                    value="לא בטוח"
                    checked={formData.disciplineAware === 'לא בטוח'}
                    onChange={handleInputChange}
                    disabled={isSubmitting || submitted}
                  />
                  <span className={styles.radioCircle}></span>
                  <span className={styles.radioText}>לא בטוח</span>
                </label>
              </div>
              {errors.disciplineAware && <p className={styles.errorText}>{errors.disciplineAware}</p>}
            </div>

            {/* Question 5 - Open Question */}
            <div className={styles.formSection}>
              <h3 className={styles.questionTitle}>
                <span className={styles.questionNumber}>5</span>
                שאלה פתוחה
              </h3>
              <p className={styles.questionSubtext}>
                למה החלטת להתחיל ללמוד מסחר דווקא עכשיו, ומה היעד שלך בהתקדמות לחשבון ממומן?
              </p>
              <textarea
                name="openQuestion"
                value={formData.openQuestion}
                onChange={handleInputChange}
                className={styles.formTextarea}
                placeholder="ספר לנו..."
                rows="4"
                disabled={isSubmitting || submitted}
              />
            </div>

            {/* Privacy Checkbox */}
            <div className={styles.privacyCheckbox}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  name="agreed"
                  checked={agreed}
                  onChange={() => setAgreed(!agreed)}
                  disabled={isSubmitting || submitted}
                />
                <span className={styles.checkboxCustom}></span>
                <span>קראתי ואני מאשר/ת את תנאי השימוש ומדיניות הפרטיות</span>
              </label>
              {errors.agreed && <p className={styles.errorText}>{errors.agreed}</p>}
            </div>

            {/* Submit Button */}
            <div className={styles.formButtons}>
              <button
                className={`${styles.submitButton} ${isSubmitting ? styles.submitting : ''} ${submitted ? styles.submitted : ''}`}
                onClick={handleContactSubmit}
                disabled={isSubmitting || submitted}
              >
                {isSubmitting ? (
                  <>
                    <span className={styles.spinner}></span>
                    שולח...
                  </>
                ) : submitted ? (
                  <>
                    <svg viewBox="0 0 24 24" fill="currentColor" className={styles.successIcon}>
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
                    </svg>
                    נשלח בהצלחה!
                  </>
                ) : (
                  'שליחה וקבלת התאמה אישית'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FloatingButton;